<template>
    <modal
        name="share-watchlist-select-menu"
        :width="1100"
        height="auto"
        :adaptive="true"
        :scrollable="true"
        @closed="resetModal"
    >
        <div class="share-watchlist-modal">
            <div class="share-watchlist-modal__header">
                <div class="share-watchlist-modal__title-section">
                    <h1 class="share-watchlist-modal__title" v-html="$t('WatchListModal.share_watchlist_title')"></h1>
                    <TotemButton
                        v-if="isMobileView"
                        @click="toggleFilters"
                        :color="getFilterButtonColor"
                        class="share-watchlist-modal__filter-button"
                        :label="$t('WatchListModal.share_watchlist_show_filters')"
                    />
                </div>
                <span class="share-watchlist-modal__title-info" v-html="getShareInstructionsForTab"></span>
            </div>
            <div class="share-watchlist-modal__top-functions">
                <template v-if="isDesktopView">
                    <ShareWatchListTabs
                        :subordinates="filteredSubordinates"
                        :members="filteredMembers"
                    />
                    <ShareWatchListDepartmentFilter :departments="getDepartmentsArray" />
                    <ShareWatchListTeamFilter :teams="getTeamsArray" />
                    <div class="share-watchlist-modal__search-input">
                        <TotemSearch :placeholder='searchPlaceholder' v-model="searchString" v-on:clearSearch="onClearSearch" size="small"/>
                    </div>
                </template>
                <template v-else>
                    <div class="share-watchlist-modal__mobile-search-filters" v-if="showFilters">
                        <TotemSearch
                            :placeholder='searchPlaceholder'
                            class="share-watchlist-modal__search-input"
                            v-model="searchString"
                            v-on:clearSearch="onClearSearch"
                            size="small"
                        />
                        <div class="share-watchlist-modal__mobile-filters">
                            <ShareWatchListTeamFilter :teams="getTeamsArray" />
                            <ShareWatchListDepartmentFilter :departments="getDepartmentsArray" />
                        </div>
                    </div>
                    <ShareWatchListTabs
                        :subordinates="filteredSubordinates"
                        :members="filteredMembers"
                    />
                </template>
            </div>
            <template v-if="shareWatchListActiveTab === 1">
                <ShareWatchListTable
                    v-if="isDesktopView"
                    :subordinates="filteredSubordinates"
                    :hasTeams="getTeamsArray.length > 0"
                    :hasDepartments="getDepartmentsArray.length > 0"
                />
                <ShareWatchListTableMobile v-if="isMobileView" :subordinates="filteredSubordinates" />
                <ShareWatchListBottomFunctions v-on:shareWithSelectedSubordinates="shareWithSelectedSubordinates" />
            </template>
            <template v-if="shareWatchListActiveTab === 2">
                <ShareWatchListTable
                    v-if="isDesktopView"
                    :subordinates="filteredMembers"
                    :hasTeams="getTeamsArray.length > 0"
                    :hasDepartments="getDepartmentsArray.length > 0"
                />
                <ShareWatchListTableMobile v-if="isMobileView" :subordinates="filteredMembers" />
                <ShareWatchListBottomFunctions v-on:removeSelectedSubordinates="removeSelectedSubordinates" />
            </template>
        </div>
    </modal>
</template>

<script>
    import { mapGetters } from 'vuex'
    import ShareWatchListTeamFilter from 'components/user/watchlist/sharewatchlist/selectusers/ShareWatchListTeamFilter'
    import ShareWatchListDepartmentFilter from 'components/user/watchlist/sharewatchlist/selectusers/ShareWatchListDepartmentFilter'
    import ShareWatchListTable from 'components/user/watchlist/sharewatchlist/selectusers/ShareWatchListTable'
    import ShareWatchListTableMobile from 'components/user/watchlist/sharewatchlist/selectusers/mobile/ShareWatchListTableMobile'
    import ShareWatchListBottomFunctions from 'components/user/watchlist/sharewatchlist/selectusers/ShareWatchListBottomFunctions'
    import ShareWatchListTabs from 'components/user/watchlist/sharewatchlist/selectusers/ShareWatchListTabs'

    export default {
        name: 'ShareWatchListModal',
        components: {
            ShareWatchListTeamFilter,
            ShareWatchListDepartmentFilter,
            ShareWatchListTable,
            ShareWatchListTableMobile,
            ShareWatchListBottomFunctions,
            ShareWatchListTabs
        },
        data () {
            return {
                selectAllUsers: false,
                searchString: '',
                showFilters: false
            }
        },
        props: {
            isDemo: Boolean,
            selectedWatchList: Number
        },
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                teamFilter: 'Watchlists/getShareWatchListTeamFilter',
                departmentFilter: 'Watchlists/getShareWatchListDepartmentFilter',
                selectedSubordinates: 'Watchlists/getShareWatchListSelectedSubordinates',
                shareWatchListActiveTab: 'Watchlists/getShareWatchListTab',
                watchLists: 'Watchlists/getWatchLists',
                subordinates: 'Watchlists/getSubordinates',
                userId: 'getUserId'
            }),
            searchPlaceholder () {
                return this.$t('WatchListModal.search_placeholder')
            },
            filteredSubordinates () {
                if (this.subordinates === null) {
                    return []
                }
                let filters = [
                    this.filterByTeam,
                    this.filterByDepartment,
                    this.filterBySearch,
                    this.filterBySharedMembers
                ]
                let filteredData = filters.reduce((acc, filter, index) => {
                    if (filter === null) {
                        return acc
                    }
                    return filter(acc)
                }, this.subordinates)
                return filteredData
            },
            filteredMembers () {
                let membersData = null
                if (this.currentWatchList === undefined || !this.currentWatchList.users) {
                    membersData = []
                } else {
                    membersData = this.currentWatchList.users
                }

                let filters = [
                    this.filterByTeam,
                    this.filterByDepartment,
                    this.filterBySearch
                ]
                let filteredData = filters.reduce((acc, filter, index) => {
                    if (filter === null) {
                        return acc
                    }
                    return filter(acc)
                }, membersData)
                return filteredData
            },
            currentWatchList () {
                return this.watchLists.find(watchList => watchList.id === this.selectedWatchList)
            },
            getTeamsArray () {
                if (this.subordinates === null) {
                    return []
                }
                let subordinateTeams = Array.from(this.subordinates).map(subordinate => subordinate.team)
                    .filter(team => this.isValueNotEmpty(team))
                return [...new Set(subordinateTeams)]
            },
            getDepartmentsArray () {
                if (this.subordinates === null) {
                    return []
                }
                let subordinateDepartments = Array.from(this.subordinates).map(subordinate => subordinate.department)
                    .filter(department => this.isValueNotEmpty(department))
                return [...new Set(subordinateDepartments)]
            },
            getShareInstructionsForTab () {
                return this.shareWatchListActiveTab === 1 ? this.$t('WatchListModal.share_watchlist_title_info')
                    : this.$t('WatchListModal.share_watchlist_title_info_remove')
            },
            getFilterButtonColor () {
                return this.showFilters ? 'primary' : 'secondary'
            }
        },
        methods: {
            filterByTeam (data) {
                if (this.teamFilter === null) {
                    return data
                }
                return data.filter(subordinate => subordinate.team === this.teamFilter)
            },
            filterByDepartment (data) {
                if (this.departmentFilter === null) {
                    return data
                }
                return data.filter(subordinate => subordinate.department === this.departmentFilter)
            },
            filterBySearch (data) {
                if (this.searchString === '') {
                    return data
                }
                return data.filter(subordinate => this.getSubordinateFullName(subordinate).toLowerCase().search(this.searchString.toLowerCase()) !== -1)
            },
            filterBySharedMembers (data) {
                if (this.currentWatchList === undefined) {
                    return data
                }
                if (this.currentWatchList.users.length === 0) {
                    return data
                }
                return data.filter(subordinate => !this.isSubordinateInMembers(subordinate.userId))
            },
            isSubordinateInMembers (userId) {
                return this.currentWatchList.users.some(member => member.userId === userId)
            },
            getSubordinateFullName (subordinate) {
                return subordinate.firstName + ' ' + subordinate.lastName + ' ' + subordinate.email
            },
            removeSelectedSubordinates () {
                this.$store.dispatch('Watchlists/setShareWatchListErrorMessage', null)
                this.$emit('removeSelectedSubordinates')
            },
            shareWithSelectedSubordinates () {
                this.$store.dispatch('Watchlists/setShareWatchListErrorMessage', null)
                this.$emit('shareWithSelectedSubordinates')
            },
            onClearSearch () {
                this.searchString = ''
            },
            resetModal () {
                this.searchString = ''
                this.selectAllUsers = false
                this.$store.dispatch('Watchlists/shareWatchListChangeTab', 1)
                this.$store.dispatch('Watchlists/shareWatchListAddAllSubordinates', [])
                this.$store.dispatch('Watchlists/shareWatchListAddAllMembers', [])
                this.$store.dispatch('Watchlists/selectTeam', null)
                this.$store.dispatch('Watchlists/selectDepartment', null)
                this.$store.dispatch('Watchlists/setShareWatchListErrorMessage', null)
            },
            isValueNotEmpty (value) {
                return value !== undefined && value !== null && value !== ''
            },
            toggleFilters () {
                this.showFilters = !this.showFilters
            }
        }
    }
</script>

<style lang="scss" scoped>
    .share-watchlist-modal {
        width: 100%;

        [class*="mobile"] & {
            padding: 0;
        }

        &__header {
            margin: 0 0 20px;

            [class*="mobile"] & {
                padding: 0 15px;
            }
        }
        &__title {
            font-size: 2.2rem;
            font-family:  PlusJakartaSans-Medium, sans-serif;
            margin: 0 0 20px;

            [class*="mobile"] & {
                margin: 10px 0;
            }
        }
        &__title-section {
            [class*="mobile"] & {
                display: flex;
                flex-direction: row;
            }
        }
        &__title-info {
            font-size: 1.4rem;
            line-height: 150%;
            font-family: PlusJakartaSans-Regular, sans-serif;
            margin: 20px 0;
        }

        &__top-functions {
            justify-content: flex-end;
            display: flex;
            align-items: flex-end;
            [class*="mobile"] & {
                flex-direction: column;
            }
        }

        &__search-input {
            border-bottom: 1px solid $emperor;

            > span {
                margin-bottom: 5px;

                [class*="desktop"] & {
                    width: 150px;
                }
            }

            [class*="mobile"] & {
                border-bottom: none;
                padding: 5px 10px;
            }
        }

        &__mobile-search-filters {
            position: absolute;
            width: 95%;
            top: 65px;
            background: $gallery;
            z-index: 2;

            [class*="mobile_sm"] & {
                padding: 7px 0;
            }
        }

        &__mobile-filters {
            display: flex;
            padding: 0 10px;
        }
        &__filter-button {
            margin-left: auto;
            margin-top: 5px;
            order: 2;
        }
    }
</style>
